"use client";

import * as React from "react";
import { RootState } from "@/store/store";
import { useDispatch, useSelector } from "react-redux";
import { User } from "@/components/Types";
import { LANGUAGES, UI_LOCALE_LANGUAGES } from "@/lib/constants";
import { createContext, useContext, useState, useEffect } from "react";
import { UiStrings } from "@/translations/UiStrings";
import { UiStringsEn } from "@/translations/UiStringsEn";
import { getCookie, setCookie } from "cookies-next";
import { isBlank } from "@/lib/utils";
import { setLocale } from "@/store/reducers/appSlice";
import { enGB, ar, es, ru, Locale } from "date-fns/locale";

const localeMap = {
  spanish: "UiStringsEs",
  english: "UiStringsEn",
  russian: "UiStringsRu",
  arabic: "UiStringsAr",
};

const dateLocaleMap = {
  spanish: es,
  english: enGB,
  russian: ru,
  arabic: ar,
};

// Create context for locale
type LocaleContextType = {
  locale: string;
  t: UiStrings; // typeof UiStringsEn (for development)
  dateLocale: Locale;
};

export const LocaleContext = createContext<LocaleContextType>({
  locale: 'english',
  t: UiStringsEn,
  dateLocale: enGB,
});

export interface LocaleProviderProps {
  children: React.ReactNode;  
}

const allowedLocales = Object.keys(UI_LOCALE_LANGUAGES);

export function UserLocaleProvider({ children }: LocaleProviderProps) {
  const LOGTAG = 'UserLocaleProvider';

  // Get the value of the 'locale' cookie
  let cookieLocale: string | null = getCookie('locale') as string | null;
  
  if (isBlank(cookieLocale) || cookieLocale == "null" || cookieLocale == "undefined") {
    cookieLocale = null;
  }

  const currentUser = useSelector((state: RootState) => state.app.currentUser) as User | null;
  const stateLocale = useSelector((state: RootState) => state.app.locale) as keyof typeof UI_LOCALE_LANGUAGES;
  const [dateLocale, setDateLocale] = useState(enGB);
  const [uiStrings, setUiStrings] = useState<UiStrings | null>(UiStringsEn);
  const [locale, setStateLocale] = useState('english');  
  const [verifiedUserMotherLanguage, setVerifiedUserMotherLanguage] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    // console.log(LOGTAG, "ml:", currentUser?.motherLanguage, "sl:", stateLocale, "cl:", cookieLocale);    
    
    let languageName = stateLocale || currentUser?.motherLanguage;
    let newLocale = cookieLocale || currentUser?.motherLanguage || 'english'; // Default to English

    if (languageName == locale) {
      return;
    }

    // Match the language name to the locale
    if (languageName) {
      newLocale = UI_LOCALE_LANGUAGES[languageName as keyof typeof UI_LOCALE_LANGUAGES].name;
    }

    // Ensure the locale is allowed, if not fallback to English
    if (!allowedLocales.includes(newLocale)) {
      newLocale = 'english';
    }

    setStateLocale(newLocale);

    // Lazy load the appropriate language strings module
    const loadLanguageStrings = async () => {
      try {
        const moduleName = localeMap[newLocale as keyof typeof localeMap];        
        const module = await import(`@/translations/${moduleName}`);
        setUiStrings(module[`${moduleName}`]);
      } catch (error) {
        console.error(`Failed to load language strings for ${newLocale}`, error);
      }      
    };

    // console.log("🚀 Loading language strings", newLocale);
    loadLanguageStrings();

    // Lazy load the appropriate date locale
    const loadDateLocale = async () => {
      try {        
        setDateLocale(dateLocaleMap[newLocale as keyof typeof dateLocaleMap]);
      } catch (error) {
        console.error(`Failed to load date locale for ${newLocale}`, error);
      }
    };

    loadDateLocale();            
  }, [currentUser?.motherLanguage, stateLocale, verifiedUserMotherLanguage]);

  useEffect(() => {
    if (currentUser && !verifiedUserMotherLanguage) {
      // In some cases, the locale cookie gets lost and is reset to default "english, 
      // so if when the user becomes available, we need to verify that the mother language is not English
      if ((cookieLocale == "english" || cookieLocale == null) && currentUser.motherLanguage != cookieLocale) {
        setCookie('locale', currentUser.motherLanguage, { maxAge: 60 * 60 * 24 * 30 * 6 });
        dispatch(setLocale(currentUser.motherLanguage as keyof typeof UI_LOCALE_LANGUAGES));
      }
      setVerifiedUserMotherLanguage(true);
    }
  }, [currentUser, verifiedUserMotherLanguage, cookieLocale]);
  
  return (
    <LocaleContext.Provider value={{ locale, t: uiStrings || UiStringsEn, dateLocale }}>
      {children}
    </LocaleContext.Provider>
  );
}
